import type { CopilotHistoryChangedEventV1 } from '@streamlain/copilot-realtime-contract';

export interface HistoryView {
  sessionId: string | undefined;
  docId: string | undefined;
  showActions: boolean;
}

export function historyChangeAffectsView(
  event: CopilotHistoryChangedEventV1,
  view: HistoryView
): boolean {
  if (view.sessionId && event.sessionId === view.sessionId) return true;
  return view.showActions && !!view.docId && event.docId === view.docId;
}

/**
 * Runs a history refresh when one is requested and refreshing is safe;
 * otherwise remembers it and runs it once flush() finds it safe again.
 * At most one refresh is in flight: requests arriving meanwhile collapse
 * into a single trailing refresh, so a burst of pushes costs two reads.
 */
export class HistoryRefreshScheduler {
  private pending = false;
  private inFlight = false;
  private trailing = false;

  constructor(
    private readonly isSafe: () => boolean,
    private readonly refresh: () => Promise<void>
  ) {}

  get hasPending(): boolean {
    return this.pending;
  }

  request(): void {
    if (!this.isSafe()) {
      this.pending = true;
      return;
    }
    this.pending = false;
    if (this.inFlight) {
      this.trailing = true;
      return;
    }
    this.inFlight = true;
    // The caller owns error reporting; a rejection here stays unhandled
    // (loud) instead of being swallowed by the scheduler.
    void this.refresh().finally(() => {
      this.inFlight = false;
      if (this.trailing) {
        this.trailing = false;
        this.request();
      }
    });
  }

  flush(): void {
    if (this.pending && this.isSafe()) {
      this.request();
    }
  }
}
